import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
	DndContext, 
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	closestCorners,
	rectIntersection,
  } from '@dnd-kit/core';
  import {
	arrayMove,
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
  } from '@dnd-kit/sortable';
import { useInput, useDataProvider, useArrayInput, useFormContext, useTranslate } from 'react-admin';
import { Card, Typography, CardContent, TextField as MUITextField, Button, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';
import { translations } from "../../../i18n/i18n";
import { generateUUID, getImageURLToBlob } from './FileManager.utils';
import { useFormState } from 'react-final-form';
import { useFieldArray } from 'react-final-form-arrays';
import { Alert } from '@material-ui/lab'
import { useStyles } from "./FileManager.styles";

function customCollisionDetection(args) {
	const rectIntersections = rectIntersection(args);
  
	if (rectIntersections.length) {
		return rectIntersections;
	}
  
	return closestCorners(args);
}

const useDatastore = (inputSource, inputSourcePreviewKey) => {
	//We initialize a new input (but do not display) in the react-admin form and will use it as a source of truth
	const {
        id,
        input: { onChange, value, ...inputProps },
        meta,
        isRequired,
    } = useInput({
		multiple: true,
        source: inputSource,
		type: 'file',
        validate: undefined,

		//We need to have this field dirty to enable save button when an item is updated
		initialValue: null,
    });

	const uploadInputValue = value;
	const setUploadInputValue = onChange;

	if(Array.isArray(value) && value.find(item => !Object.keys(item).includes('uid')) !== undefined) {
		//We must assign an uid to the values loaded from the db and set the preview url
		setUploadInputValue(uploadInputValue.map(item => {
			if(!('uid' in Object.keys(item))) {
				return {
					...item,
					uid: generateUUID(),
					isUploaded: true,
					previewUrl: item[inputSourcePreviewKey],
					name: item.filename,
					size: null,
				};
			} else {
				return item;
			}
		}))
	}
	
	const addItems = (data) => {
		setUploadInputValue([...uploadInputValue, ...data.map(item => ({
			uid: generateUUID(),
			isUploaded: false,
			caption: '',
			previewUrl: getImageURLToBlob(item.file),
			name: item.file.name,
			size: item.file.size,
			...item,
		}))]);
	};
	const updateItem = (uid, updatePayload) => {
		let moveToUid = updatePayload.moveToUid;
		if('moveToUid' in updatePayload) {
			delete updatePayload.moveToUid;
		}

		const updatedArray = uploadInputValue.map(item => {
			if(item.uid !== uid) {
				return item;
			} else {
				return {
					...item,
					...updatePayload,
				};
			}
		});

		if(moveToUid) {
			const oldIndex = uploadInputValue.findIndex(item => item.uid === uid);
			const newIndex = uploadInputValue.findIndex(item => item.uid === moveToUid);
			setUploadInputValue(arrayMove(updatedArray, oldIndex, newIndex));
		} else {
			setUploadInputValue(updatedArray);
		}
	};
	const removeItem = (uid) => {
		setUploadInputValue(uploadInputValue.filter(item => item.uid !== uid));
	};
	const moveItem = (fromUid, toUid) => {
		const oldIndex = uploadInputValue.findIndex(item => item.uid === fromUid);
		const newIndex = uploadInputValue.findIndex(item => item.uid === toUid);

		if(oldIndex !== newIndex && oldIndex !== -1 & newIndex !== -1) {
			setUploadInputValue(arrayMove(uploadInputValue, oldIndex, newIndex));
		}
	};

	return {
		items: Array.isArray(uploadInputValue) ? uploadInputValue : [],
		addItems,
		updateItem,
		removeItem,
		moveItem,
	};
};

//Create view - we only append a file input to react-admin form
//Edit view - we upload the file without the react-admin form being saved
const FileManagerContext = ({inputSource, inputSourcePreviewKey, children, filesGroupIdKey = null}) => {
	const classes = useStyles();
	const translate = useTranslate();
	const inputRef = useRef();
	const fileSelectorTransformRef = useRef();
	const [isRemoveConfirmOpen, setRemoveConfirmOpen] = useState(false);
	const [removeConfirmItem, setRemoveConfirmItem] = useState({});
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	const {items, addItems, updateItem, removeItem, moveItem} = useDatastore(inputSource, inputSourcePreviewKey);
	
	//Sorting
	const handleDragEnd = (event) => {
		//Id is the uid
		const {active, over} = event;
		
		if (active.id !== over.id) {
			//Move the item if is in the same group
			if(active.data.current.sortable.containerId === over.data.current.sortable?.containerId) {
				moveItem(active.id, over.id);
			}
		}
	};
	const onDragOver = (event) => {
		//Id is the uid
		const {active, over} = event;
		
		if (active.id !== over.id) {
			//Move the item if is NOT in the same group
			if(active.data.current.sortable.containerId !== over.data.current.sortable?.containerId) {
				if(over.data.current.sortable) {
					//Dragged over a sortable
					const updatePayload = filesGroupIdKey != null ? {[filesGroupIdKey]: over.data.current.sortable.containerId} : {};
					updateItem(active.id, {...updatePayload, moveToUid: over.id });
				} else {
					//Dragged over a dropable
					const updatePayload = filesGroupIdKey != null ? {[filesGroupIdKey]: over.id} : {};
					updateItem(active.id, updatePayload);
				}
			}
		}
	};

	//Adding files
	const openFileSelector = useCallback((transformFiles) => {
		inputRef.current.value = '';
    	inputRef.current.click();
		fileSelectorTransformRef.current = transformFiles;
	}, []);
	const handleFilesInputChange = (event) => {
		addItems(Array.from(event.target.files).map(fileSelectorTransformRef.current));
	};
	const handleFileDrop = (event, transformFiles) => {
		const files = event.dataTransfer?.files;

		if(files) {
			addItems(Array.from(files).map(transformFiles));
		}
	};

	//Editing file data and removing
	const handleFileDataChange = (uid, key, value) => {
		updateItem(uid, {[key]: value});
	};
	const confirmRemoveItem =  useCallback((item) => {
		setRemoveConfirmItem(item);
		setRemoveConfirmOpen(true);
	}, []);

	//editnél az original photos tömb (amit ugye nem módosítunk) nem fogja felülírni
	//edit uploadkor ordert is küldjünk és akkor be lehet tenni a megfelelő helyre

	if(Array.isArray(items) && items.find(item => !Object.keys(item).includes('uid')) !== undefined) {
		return null;
	}

	return (
		<DndContext 
			sensors={sensors}
			collisionDetection={customCollisionDetection}
			onDragEnd={handleDragEnd}
			onDragOver={onDragOver}
		>
			<Dialog
				open={isRemoveConfirmOpen}
				onClose={() => setRemoveConfirmOpen(false)}
			>
				<DialogTitle>{translate('ra.fileManager.deleteConfirm.title')}</DialogTitle>
				<DialogContent>
					{translate(removeConfirmItem.isUploaded ? 'ra.fileManager.deleteConfirm.uploadedInfo' : 'ra.fileManager.deleteConfirm.notUploadedInfo')}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRemoveConfirmOpen(false)} color="default" variant="outlined">{translate('ra.fileManager.deleteConfirm.cancel')}</Button>
					<Button onClick={() => {
						removeItem(removeConfirmItem.uid);
						setRemoveConfirmOpen(false);
					}} color="primary" variant="contained">{translate('ra.fileManager.deleteConfirm.delete')}</Button>
				</DialogActions>
			</Dialog>
			<Alert severity="info" className={classes.alert}>{translate('ra.fileManager.behaviourAlert')}</Alert>
			{React.cloneElement(children, {items, addItems, handleFileDataChange, handleFileDrop, confirmRemoveItem, moveItem, openFileSelector})}
			<input
				multiple
				type="file"
				ref={inputRef}
				style={{display: 'none'}}
				//accept={accept.map((type) => `${acceptType}/${type}`)}
				onChange={handleFilesInputChange}
			/>
		</DndContext>
	);
};

export default FileManagerContext;